/* ============================================================================
   MAPA EN UN ARCHIVO

   Todo lo anterior chocó contra lo mismo: los servicios de mosaicos por API
   prohíben la descarga masiva, y sin ella no hay mapa donde nunca se navegó.

   La salida no es pelear con esa restricción: es **no depender de un servicio**.

   ══════════════════════════════════════════════════════════════════════════
   UN SOLO ARCHIVO CON EL MAPA ADENTRO

   Un `.pmtiles` contiene todos los mosaicos de una región. Se copia al
   dispositivo una vez y el mapa funciona para siempre:

     · Sin cobertura, sin cuota y sin clave.
     · Sin política que violar: el archivo es del operador.
     · Sin que se venza ni lo expulse el caché del navegador.

   El GPS **sigue funcionando**: es satelital y no necesita señal de celular.
   Lo único que faltaba sin cobertura eran los mosaicos, y esto los resuelve.

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ PMTILES Y NO MBTILES

   MBTiles es el formato clásico, pero es una base SQLite: leerla en el
   navegador exige cargar un motor de base de datos entero en memoria.

   PMTiles guarda lo mismo en un archivo pensado para leerse **por pedazos**.
   Se abre el archivo y se leen solo los mosaicos que el mapa pide, sin cargar
   nada más. Un archivo de 2 GB se usa igual que uno de 20 MB.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

const texto = (v) => (typeof v === "string" ? v.trim() : "");

/** Dónde se recuerda el archivo elegido. */
export const CLAVE_ARCHIVO = "hcMapaArchivo";

/**
 * ¿Este archivo sirve?
 *
 * Se comprueba **antes** de intentar abrirlo: un mensaje claro acá evita que
 * el piloto crea que la aplicación está rota cuando eligió el archivo
 * equivocado.
 */
export function evaluarArchivo(archivo) {
  if (!archivo || typeof archivo !== "object") {
    return { sirve: false, motivo: "No se eligió ningún archivo." };
  }

  const nombre = texto(archivo.name).toLowerCase();
  const bytes = Number(archivo.size) || 0;

  if (nombre.endsWith(".mbtiles")) {
    return {
      sirve: false,
      motivo: "Es un archivo MBTiles. Convertilo a PMTiles con la herramienta "
        + "«pmtiles convert», que es gratuita, y vuelve a intentar.",
    };
  }

  if (!nombre.endsWith(".pmtiles")) {
    return {
      sirve: false,
      motivo: `«${archivo.name}» no es un mapa. Hace falta un archivo .pmtiles.`,
    };
  }

  /* Un archivo de pocos kilobytes no puede tener un mapa: o se cortó la
     descarga, o es un marcador de posición. Decirlo ahora evita que el piloto
     descubra en vuelo que el mapa está vacío. */
  if (bytes < 100 * 1024) {
    return {
      sirve: false,
      motivo: `El archivo pesa ${Math.round(bytes / 1024)} KB: es demasiado chico `
        + "para tener un mapa. Puede que la descarga se haya cortado.",
    };
  }

  return {
    sirve: true,
    motivo: null,
    mb: Math.round(bytes / 1024 / 1024),
  };
}

/**
 * Qué cubre el archivo, leído de su cabecera.
 *
 * Sirve para lo que más importa antes de un vuelo: **saber si la zona a la que
 * se va está adentro**. Un archivo de Perú no ayuda volando en Ecuador, y
 * descubrirlo en el aire es tarde.
 */
export function resumenDeCobertura(cabecera) {
  if (!cabecera || typeof cabecera !== "object") return null;

  const norte = Number(cabecera.maxLat);
  const sur = Number(cabecera.minLat);
  const oeste = Number(cabecera.minLon);
  const este = Number(cabecera.maxLon);

  if ([norte, sur, oeste, este].some((v) => !Number.isFinite(v))) return null;

  return {
    norte, sur, oeste, este,
    /* ═══ `Number(x) ?? null` NUNCA DA NULL ═══

       `Number(undefined)` es `NaN`, y `NaN` no es nullish: el `??` no se
       dispara nunca. Un archivo que no declarara su zoom guardaba `NaN`, que
       en pantalla se muestra como «NaN» y en una comparación da siempre falso.

       Lo agarró el lint al retomar el proyecto, no las pruebas: ninguna
       ejercitaba una cabecera sin zoom. */
    zoomMin: Number.isFinite(Number(cabecera.minZoom)) ? Number(cabecera.minZoom) : null,
    zoomMax: Number.isFinite(Number(cabecera.maxZoom)) ? Number(cabecera.maxZoom) : null,
    /* El detalle máximo dice para qué sirve: hasta zoom 10 es navegación
       gruesa; de 13 para arriba se ven caminos y helipuertos. */
    detalle: describirDetalle(Number(cabecera.maxZoom)),
  };
}

function describirDetalle(zoomMax) {
  if (!Number.isFinite(zoomMax)) return null;
  if (zoomMax <= 8) return "Vista general. Sirve para ubicarse, no para navegar fino.";
  if (zoomMax <= 11) return "Navegación de ruta. Poblados, ríos y relieve.";
  if (zoomMax <= 13) return "Detalle bueno. Caminos y accidentes del terreno.";
  return "Detalle alto. Se ven caminos secundarios y construcciones.";
}

/**
 * ¿Un punto está dentro de lo que cubre el archivo?
 *
 * Se usa para avisar antes de despegar, no para impedir nada: el piloto puede
 * volar igual, pero tiene que saber que el mapa se le va a acabar.
 */
export function cubrePunto(cobertura, lat, lon) {
  if (!cobertura) return null;
  const la = Number(lat);
  const lo = Number(lon);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;

  return la <= cobertura.norte && la >= cobertura.sur
    && lo >= cobertura.oeste && lo <= cobertura.este;
}

/**
 * ¿La ruta entera está cubierta?
 *
 * Devuelve los puntos que quedan afuera. Volar hasta el borde del mapa y
 * seguir a ciegas es exactamente lo que este módulo existe para evitar.
 */
export function cubreRuta(cobertura, puntos = []) {
  if (!cobertura) return { cubre: null, afuera: [] };

  const lista = Array.isArray(puntos) ? puntos : [];
  const afuera = lista.filter((p) => cubrePunto(cobertura, p?.lat, p?.lon) === false);

  return {
    cubre: afuera.length === 0,
    afuera,
    motivo: afuera.length > 0
      ? `${afuera.length} ${afuera.length === 1 ? "punto de la ruta queda" : "puntos de la ruta quedan"} `
        + "fuera del mapa descargado. Ahí no vas a ver nada."
      : null,
  };
}

/** Dónde conseguir un archivo, para que el operador no tenga que buscarlo. */
export const DE_DONDE_SACARLO = [
  {
    nombre: "MapTiler Data",
    url: "https://data.maptiler.com/downloads/planet/",
    nota: "Si ya tienes cuenta en MapTiler, mira si tu plan incluye descargas. "
      + "Los paquetes vienen listos y con licencia para servirlos tú.",
    pago: true,
  },
  {
    nombre: "Protomaps",
    url: "https://maps.protomaps.com/builds/",
    nota: "Extractos del mundo entero en PMTiles, de datos abiertos y gratis. "
      + "También puedes recortar solo tu región con su herramienta.",
    pago: false,
  },
  {
    nombre: "Geofabrik",
    url: "https://download.geofabrik.de/",
    nota: "Datos por país y región. Hace falta convertirlos a PMTiles.",
    pago: false,
  },
];
